/**
 * Calendar — everything a client has on a given day in one place: training
 * sessions (appointments), assigned cardio (projected from its weekday), and
 * that day's macro target. The assigned program name sits at the top (program
 * workouts have no per-day schedule, so we surface the program, not a per-date
 * workout).
 *
 * Default view is a 7-day strip (today + 6); "Month" expands to a full grid the
 * user can tap any date in. Reuses existing endpoints:
 *   GET /api/appointments
 *   GET /api/workouts/cardio-plan
 *   GET /api/user/dashboard         (program name)
 *   GET /api/nutrition/today?date=  (that date's resolved macro target)
 */

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Appointment } from '@regis/shared';
import { Dumbbell, CalendarDays, CalendarRange, Rows3, Clock, HeartPulse, UtensilsCrossed, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, Muted } from '@/components/ui';

// ── date helpers (local, no UTC drift) ──────────────────────────────────────
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}
function dayParts(iso: string): { wd: string; day: string; full: string } {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    wd: dt.toLocaleDateString(undefined, { weekday: 'short' }),
    day: dt.toLocaleDateString(undefined, { day: 'numeric' }),
    full: dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }),
  };
}
function label12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

interface Prescription {
  id: string;
  machine_id: string | null;
  activity_name: string | null;
  target_minutes: number | null;
  target_distance: number | null;
  day_of_week: number | null;
  frequency_per_week: number | null;
}
interface DashboardLite {
  currentProgram: { id: string; name: string; currentWeek: number | null } | null;
}
interface WeeklyDayTarget {
  day_of_week: number; dayApplied: boolean;
  calories: number; protein: number; carbs: number; fat: number; waterOz: number;
}

const DAYS = 7;
const AGENDA_DAYS = 14; // how many upcoming days the stacked agenda shows

type View = 'week' | 'agenda' | 'month';

export default function CalendarPage() {
  const [activeDay, setActiveDay] = useState(todayLocal());
  const [view, setView] = useState<View>('agenda');

  const appts = useQuery({
    queryKey: ['appointments'],
    queryFn: () => api.get<{ appointments: Appointment[] }>('/api/appointments'),
  });
  const cardio = useQuery({
    queryKey: ['cardio-plan'],
    queryFn: () => api.get<{ prescriptions: Prescription[] }>('/api/workouts/cardio-plan'),
  });
  // Activity list — lets the client substitute the prescribed activity for what
  // they actually did, exactly like the full /workouts/cardio page.
  const machines = useQuery({
    queryKey: ['machines'],
    queryFn: () => api.get<{ machines: { id: string; name: string }[] }>('/api/workouts/machines'),
  });
  const dash = useQuery({
    queryKey: ['dashboard-program'],
    queryFn: () => api.get<DashboardLite>('/api/user/dashboard'),
  });
  // The coach's resolved 7-day macro plan, fetched ONCE. Look up any date's
  // macros by weekday — powers both the agenda rows and the day detail.
  const weekly = useQuery({
    queryKey: ['weekly-targets'],
    queryFn: () => api.get<{ days: WeeklyDayTarget[]; anyDayApplied: boolean }>('/api/nutrition/weekly-targets'),
  });
  const macroFor = (d: string): WeeklyDayTarget | undefined =>
    weekly.data?.days.find((x) => x.day_of_week === weekdayOf(d));

  // Which cardio machines the user already logged, per day (for "✓ Done") across
  // the whole agenda window — one fetch, looked up by date.
  const qc = useQueryClient();
  const cardioLogged = useQuery({
    queryKey: ['cardio-logged'],
    queryFn: () => api.get<{ byDate: Record<string, string[]> }>('/api/workouts/cardio-logged'),
  });
  const isCardioDoneOn = (d: string, p: Prescription) =>
    !!p.machine_id && (cardioLogged.data?.byDate?.[d] ?? []).includes(p.machine_id);

  // Inline cardio logging — POST to the existing /api/workouts/logs endpoint. The
  // form carries the date so logging works from any day (agenda or detail).
  const [logForm, setLogForm] = useState<{
    date: string; p: Prescription; machineId: string;
    minutes: string; distance: string; calories: string; heartRate: string; rpe: string;
  } | null>(null);
  const openLog = (d: string, p: Prescription) =>
    setLogForm({
      date: d, p, machineId: p.machine_id ?? '',
      minutes: p.target_minutes ? String(p.target_minutes) : '',
      distance: '', calories: '', heartRate: '', rpe: '',
    });
  const logCardio = useMutation({
    mutationFn: (f: NonNullable<typeof logForm>) =>
      api.post('/api/workouts/logs', {
        date: f.date,
        workout_type: 'cardio',
        duration_minutes: f.minutes ? parseInt(f.minutes) : null,
        notes: machines.data?.machines.find((m) => m.id === f.machineId)?.name ?? f.p.activity_name ?? null,
        cardio_blocks: [{
          machine_id: f.machineId || f.p.machine_id,
          prescription_id: f.p.id,
          actual_minutes: f.minutes ? parseFloat(f.minutes) : null,
          actual_distance: f.distance ? parseFloat(f.distance) : null,
          actual_calories: f.calories ? parseFloat(f.calories) : null,
          actual_avg_heart_rate: f.heartRate ? parseFloat(f.heartRate) : null,
          rpe: f.rpe ? parseInt(f.rpe) : null,
        }],
      }),
    onSuccess: () => {
      setLogForm(null);
      qc.invalidateQueries({ queryKey: ['cardio-logged'] });
    },
  });

  const programName = dash.data?.currentProgram?.name ?? null;

  const dates = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(todayLocal(), i)), []);

  // Days (in the 7-day strip) that have any session — for the marker dot.
  const apptDates = useMemo(() => {
    const s = new Set<string>();
    for (const a of appts.data?.appointments ?? []) {
      if (a.status === 'upcoming') s.add(a.date);
    }
    return s;
  }, [appts.data]);

  // Sessions + cardio for ANY date (reused by the single-day detail and each
  // agenda row). Frequency-based cardio has no weekday, so it isn't day-bound.
  const sessionsOn = (d: string) =>
    (appts.data?.appointments ?? [])
      .filter((a) => a.date === d && a.status !== 'cancelled')
      .sort((x, y) => x.start_time.localeCompare(y.start_time));
  const cardioOn = (d: string) =>
    (cardio.data?.prescriptions ?? []).filter((p) => p.day_of_week === weekdayOf(d));
  const freqCardio = (cardio.data?.prescriptions ?? []).filter((p) => p.day_of_week == null && p.frequency_per_week);

  const daySessions = sessionsOn(activeDay);
  const dayCardio = cardioOn(activeDay);
  const dayMacro = macroFor(activeDay);

  const agendaDates = useMemo(
    () => Array.from({ length: AGENDA_DAYS }, (_, i) => addDays(todayLocal(), i)),
    []
  );

  return (
    <>
      <PageHeader title="Calendar" subtitle="Your plan, day by day" />
      <div className="px-4 flex flex-col gap-4" style={{ minWidth: 0 }}>
        {/* Assigned program banner */}
        <Card style={{ background: 'linear-gradient(135deg, rgba(201,168,106,0.12), var(--card-bg))' }}>
          <div className="flex items-center gap-2">
            <Dumbbell size={15} style={{ color: 'var(--champagne)' }} />
            <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
              Current Program
            </span>
          </div>
          <p className="text-sm font-semibold mt-1" style={{ color: 'var(--cream)' }}>
            {dash.isLoading ? '—' : programName ?? 'No program assigned yet'}
          </p>
        </Card>

        {/* View switch: 7-day strip · stacked agenda · month grid */}
        <div style={{ display: 'inline-flex', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--card-border)', width: '100%' }}>
          {([['agenda', 'Agenda', Rows3], ['week', '7 days', CalendarDays], ['month', 'Month', CalendarRange]] as Array<[View, string, typeof CalendarDays]>).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setView(key)}
              aria-pressed={view === key}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                padding: '0.5rem', fontSize: '0.82rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                background: view === key ? 'var(--gold)' : 'transparent',
                color: view === key ? '#1a1a1a' : 'var(--text-tertiary)',
              }}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {view === 'agenda' ? (
          <AgendaList
            dates={agendaDates}
            loading={appts.isLoading || cardio.isLoading}
            sessionsOn={sessionsOn}
            cardioOn={cardioOn}
            macroFor={macroFor}
            cardioDoneOn={isCardioDoneOn}
            onLogCardio={openLog}
          />
        ) : view === 'month' ? (
          <MonthGrid activeDay={activeDay} onPick={setActiveDay} markedDates={apptDates} />
        ) : (
          <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', paddingBottom: '0.25rem', minWidth: 0 }}>
            {dates.map((d) => {
              const { wd, day } = dayParts(d);
              const isActive = d === activeDay;
              const isToday = d === todayLocal();
              const marked = apptDates.has(d);
              return (
                <button
                  key={d}
                  onClick={() => setActiveDay(d)}
                  aria-pressed={isActive}
                  style={{
                    flex: '0 0 auto', width: 52, padding: '0.5rem 0',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${isActive ? 'var(--gold)' : 'var(--card-border)'}`,
                    background: isActive ? 'var(--gold)' : 'var(--card-bg)',
                    color: isActive ? '#1a1a1a' : isToday ? 'var(--gold)' : 'var(--text-primary)',
                    cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', opacity: 0.8 }}>{wd}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.1 }}>{day}</div>
                  <div style={{ height: 6, marginTop: 2, display: 'flex', justifyContent: 'center' }}>
                    {marked && (
                      <span style={{ width: 5, height: 5, borderRadius: 999, background: isActive ? '#1a1a1a' : 'var(--gold)' }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Selected day's everything (week/month modes; agenda stacks its own) */}
        {view !== 'agenda' && (
        <div>
          <h2 className="text-base font-semibold mb-2" style={{ color: 'var(--cream)' }}>{dayParts(activeDay).full}</h2>

          {/* Training sessions */}
          <Section icon={<Clock size={14} />} title="Training sessions">
            {appts.isLoading ? (
              <Muted>Loading…</Muted>
            ) : daySessions.length === 0 ? (
              <Muted>No sessions booked.</Muted>
            ) : (
              <div className="flex flex-col gap-2">
                {daySessions.map((a) => (
                  <Row
                    key={a.id}
                    title={`${label12(a.start_time)} – ${label12(a.end_time)}`}
                    sub={[a.appointment_type, a.trainer ? `${a.trainer.first_name ?? ''} ${a.trainer.last_name ?? ''}`.trim() : null]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                ))}
              </div>
            )}
          </Section>

          {/* Cardio */}
          <Section icon={<HeartPulse size={14} />} title="Cardio">
            {cardio.isLoading ? (
              <Muted>Loading…</Muted>
            ) : dayCardio.length === 0 && freqCardio.length === 0 ? (
              <Muted>No cardio prescribed.</Muted>
            ) : (
              <div className="flex flex-col gap-2">
                {dayCardio.map((p) => (
                  <CardioRow key={p.id} p={p} done={isCardioDoneOn(activeDay, p)} onLog={() => openLog(activeDay, p)} />
                ))}
                {freqCardio.map((p) => (
                  <Row
                    key={p.id}
                    title={p.activity_name ?? 'Cardio'}
                    sub={`${p.frequency_per_week}× / week${p.target_minutes ? ` · ${p.target_minutes} min` : ''}`}
                    faded
                  />
                ))}
              </div>
            )}
          </Section>

          {/* Macro target for the day */}
          <Section icon={<UtensilsCrossed size={14} />} title="Nutrition target">
            {weekly.isLoading || !dayMacro ? (
              <Muted>Loading…</Muted>
            ) : (
              <Card>
                <MacroDesignation applied={dayMacro.dayApplied} dateLabel={dayParts(activeDay).full.split(',')[0]} />
                <div className="grid grid-cols-4 gap-2 mt-2 text-center">
                  <Macro label="kcal" value={dayMacro.calories} />
                  <Macro label="P" value={dayMacro.protein} unit="g" />
                  <Macro label="C" value={dayMacro.carbs} unit="g" />
                  <Macro label="F" value={dayMacro.fat} unit="g" />
                </div>
              </Card>
            )}
          </Section>
        </div>
        )}
      </div>

      {/* Inline cardio log sheet — portaled to <body> so the fixed overlay
          escapes AppShell's scroll/max-width containers and anchors to the true
          viewport (fixes mobile bottom-anchoring). */}
      {logForm && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', height: '100dvh', padding: 16 }}
          onClick={() => setLogForm(null)}
        >
          {/* Centered, height-capped so the whole sheet (incl. its fixed footer)
              is always within the viewport — never anchored to the bottom where
              the footer would sit behind the browser chrome. Flex column: fixed
              header + scrollable body + fixed footer. */}
          <div
            className="card w-full sm:max-w-sm rounded-2xl flex flex-col"
            style={{ maxWidth: 420, maxHeight: '100%', padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header (fixed) */}
            <div className="flex-shrink-0 px-5 pt-5 pb-2">
              <h2 className="display mb-1" style={{ fontSize: '1.4rem' }}>Log {logForm.p.activity_name ?? 'Cardio'}</h2>
              <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{dayParts(logForm.date).full}</p>
            </div>

            {/* Body (scrolls) */}
            <div className="flex-1 overflow-y-auto px-5" style={{ minHeight: 0 }}>
              {/* Activity — prescribed activity is preselected; tap to substitute. */}
              <label className="text-[11px]" style={{ color: 'var(--gray-500)' }}>Activity</label>
              <div className="flex flex-wrap gap-2 mb-3 mt-1">
                {(machines.data?.machines ?? []).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setLogForm({ ...logForm, machineId: m.id })}
                    className="px-3 py-1.5 text-xs font-medium"
                    style={{
                      borderRadius: 'var(--radius-sm)',
                      background: logForm.machineId === m.id ? 'var(--champagne)' : 'var(--surface)',
                      color: logForm.machineId === m.id ? 'var(--ink)' : 'var(--cream-muted)',
                    }}
                  >
                    {m.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <LogField label="Duration (min)" value={logForm.minutes} onChange={(v) => setLogForm({ ...logForm, minutes: v })} />
                <LogField label="Distance (mi)" value={logForm.distance} onChange={(v) => setLogForm({ ...logForm, distance: v })} />
                <LogField label="Calories" value={logForm.calories} onChange={(v) => setLogForm({ ...logForm, calories: v })} />
                <LogField label="Avg Heart Rate" value={logForm.heartRate} onChange={(v) => setLogForm({ ...logForm, heartRate: v })} />
              </div>
              <div className="mt-3 mb-2">
                <label className="text-[11px]" style={{ color: 'var(--gray-500)' }}>RPE (1-10)</label>
                <div className="flex gap-1 mt-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      onClick={() => setLogForm({ ...logForm, rpe: String(n) })}
                      className="flex-1 py-2 text-xs font-medium"
                      style={{
                        borderRadius: 'var(--radius-sm)',
                        background: logForm.rpe === String(n) ? 'var(--champagne)' : 'var(--surface)',
                        color: logForm.rpe === String(n) ? 'var(--ink)' : 'var(--cream-muted)',
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer (fixed) — always visible, never scrolls away. */}
            <div
              className="flex-shrink-0 flex gap-2 px-5 pt-3"
              style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--card-border)' }}
            >
              <button className="btn-outline flex-1" onClick={() => setLogForm(null)}>Cancel</button>
              <button
                className="btn-gold flex-1 flex items-center justify-center gap-2"
                disabled={logCardio.isPending}
                onClick={() => logCardio.mutate(logForm)}
              >
                <Check size={16} /> {logCardio.isPending ? 'Saving…' : 'Mark Complete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function CardioRow({ p, done, onLog }: { p: Prescription; done: boolean; onLog: () => void }) {
  return (
    <button onClick={onLog} className="w-full text-left">
      <Card style={done ? { borderColor: 'var(--success)' } : undefined}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{p.activity_name ?? 'Cardio'}</p>
            <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{p.target_minutes ? `${p.target_minutes} min` : 'Tap to log'}</p>
          </div>
          {done ? (
            <span className="text-xs flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
              <Check size={12} /> Done
            </span>
          ) : (
            <span className="text-xs" style={{ color: 'var(--champagne)' }}>Log ›</span>
          )}
        </div>
      </Card>
    </button>
  );
}

function LogField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-[11px]" style={{ color: 'var(--gray-500)' }}>{label}</label>
      <input
        type="number"
        inputMode="decimal"
        className="input"
        placeholder="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Agenda view — upcoming days stacked vertically; the user just scrolls, no
 * tapping. Each day with anything on it shows its sessions + cardio; empty days
 * collapse to a thin "Nothing scheduled" line so the list stays scannable.
 */
function AgendaList({
  dates, loading, sessionsOn, cardioOn, macroFor, cardioDoneOn, onLogCardio,
}: {
  dates: string[];
  loading: boolean;
  sessionsOn: (d: string) => Appointment[];
  cardioOn: (d: string) => Prescription[];
  macroFor: (d: string) => WeeklyDayTarget | undefined;
  cardioDoneOn: (d: string, p: Prescription) => boolean;
  onLogCardio: (d: string, p: Prescription) => void;
}) {
  if (loading) return <Muted>Loading…</Muted>;
  return (
    <div className="flex flex-col gap-3">
      {dates.map((d) => {
        const sessions = sessionsOn(d);
        const cardios = cardioOn(d);
        const m = macroFor(d);
        const { wd, day, full } = dayParts(d);
        const isToday = d === todayLocal();
        return (
          <div key={d}>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-sm font-semibold" style={{ color: isToday ? 'var(--gold)' : 'var(--cream)' }}>
                {wd} {day}
              </span>
              <span className="text-[11px]" style={{ color: 'var(--gray-500)' }}>{isToday ? 'Today' : full}</span>
            </div>
            <div className="flex flex-col gap-2">
              {sessions.map((a) => (
                <Row
                  key={a.id}
                  title={`${label12(a.start_time)} – ${label12(a.end_time)}`}
                  sub={[a.appointment_type, a.trainer ? `${a.trainer.first_name ?? ''} ${a.trainer.last_name ?? ''}`.trim() : null]
                    .filter(Boolean).join(' · ')}
                />
              ))}
              {cardios.map((p) => (
                <CardioRow key={p.id} p={p} done={cardioDoneOn(d, p)} onLog={() => onLogCardio(d, p)} />
              ))}
              {/* Macros line — always shown so each day's nutrition target is visible. */}
              {m && (
                <Card>
                  <div className="flex items-center justify-between">
                    <MacroDesignation applied={m.dayApplied} dateLabel={wd} small />
                    <span className="text-xs" style={{ color: 'var(--cream)' }}>
                      {Math.round(m.calories)} kcal · {Math.round(m.protein)}p / {Math.round(m.carbs)}c / {Math.round(m.fat)}f
                    </span>
                  </div>
                </Card>
              )}
              {sessions.length === 0 && cardios.length === 0 && (
                <p className="text-xs pl-0.5" style={{ color: 'var(--gray-500)' }}>No sessions or cardio.</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Badge stating whether a day's macros are a coach-set custom day or the
 *  standard everyday target. */
function MacroDesignation({ applied, dateLabel, small }: { applied: boolean; dateLabel: string; small?: boolean }) {
  return (
    <span
      className={`${small ? 'text-[9px]' : 'text-[10px]'} font-medium uppercase tracking-wider px-2 py-0.5 rounded-full`}
      style={applied
        ? { background: 'rgba(201,168,106,0.15)', color: 'var(--champagne)', border: '1px solid var(--gold-muted)' }
        : { background: 'rgba(255,255,255,0.06)', color: 'var(--gray-400)', border: '1px solid rgba(255,255,255,0.10)' }}
    >
      {applied ? `Custom · ${dateLabel}` : 'Standard'}
    </span>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-1.5" style={{ color: 'var(--gold-muted)' }}>
        {icon}
        <span className="text-[11px] font-medium uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Row({ title, sub, faded }: { title: string; sub?: string; faded?: boolean }) {
  return (
    <Card style={faded ? { opacity: 0.8 } : undefined}>
      <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{title}</p>
      {sub ? <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{sub}</p> : null}
    </Card>
  );
}

function Macro({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div>
      <p className="text-sm font-bold" style={{ color: 'var(--cream)' }}>{Math.round(value)}{unit}</p>
      <p className="text-[10px]" style={{ color: 'var(--gray-500)' }}>{label}</p>
    </div>
  );
}

// ── month grid ──────────────────────────────────────────────────────────────
function MonthGrid({
  activeDay, onPick, markedDates,
}: { activeDay: string; onPick: (d: string) => void; markedDates: Set<string> }) {
  // Month containing the active day.
  const [y, m] = activeDay.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const startPad = first.getDay(); // 0=Sun
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }

  const monthLabel = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <Card>
      <p className="text-sm font-semibold mb-2 text-center" style={{ color: 'var(--cream)' }}>{monthLabel}</p>
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <span key={i} className="text-[10px]" style={{ color: 'var(--gray-500)' }}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((iso, i) =>
          iso === null ? (
            <span key={i} />
          ) : (
            <button
              key={iso}
              onClick={() => onPick(iso)}
              style={{
                aspectRatio: '1', borderRadius: 'var(--radius-sm)', position: 'relative',
                border: `1px solid ${iso === activeDay ? 'var(--gold)' : 'transparent'}`,
                background: iso === activeDay ? 'var(--gold)' : 'transparent',
                color: iso === activeDay ? '#1a1a1a' : iso === todayLocal() ? 'var(--gold)' : 'var(--text-primary)',
                fontSize: '0.8rem', cursor: 'pointer',
              }}
            >
              {Number(iso.split('-')[2])}
              {markedDates.has(iso) && iso !== activeDay && (
                <span style={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: 999, background: 'var(--gold)' }} />
              )}
            </button>
          )
        )}
      </div>
    </Card>
  );
}
